import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Generation-progress controller for the Vocabulary Set wizard Step III.
//
// Subscribes to the `shorts_wizard_<config_id>` ActionCable channel
// (same channel VocabularySetOrchestratorService broadcasts to) and
// updates the progress bar + per-card status pills as each card is
// processed. Kicking off generation is a single fetch POST; the
// orchestrator streams progress back.
export default class extends Controller {
  static targets = ["bar", "count", "status", "startButton", "cardRow", "cardStatus"]
  static values = {
    configId: Number,
    startUrl: String,
    nextUrl: String,
  }

  connect() {
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      { channel: "ShortsWizardChannel", config_id: this.configIdValue },
      {
        received: (data) => this.handleProgress(data),
      }
    )
  }

  disconnect() {
    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
  }

  async startAll(event) {
    event?.preventDefault()
    this.setStatus("Starting…")
    this.startButtonTarget.disabled = true

    try {
      const response = await fetch(this.startUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken(),
          "Accept": "application/json",
        },
      })

      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        this.setStatus(`Error: ${body.error || response.statusText}`)
        this.startButtonTarget.disabled = false
        return
      }

      // Reload after a short delay so freshly-generated audio paths
      // render in the per-card status pills.
      setTimeout(() => {
        window.location.reload()
      }, 900)
    } catch (err) {
      this.setStatus(`Error: ${err.message}`)
      this.startButtonTarget.disabled = false
    }
  }

  handleProgress(data) {
    if (data.percentage != null) this.setProgress(data.percentage)
    if (data.message) this.setStatus(data.message)

    // When the step key matches generate_tts_<index>, mark that card as ready.
    const match = data.step?.match(/generate_tts_(\d+)/)
    if (match) {
      const index = parseInt(match[1], 10)
      const row = this.cardRowTargets.find((el) => parseInt(el.dataset.cardIndex, 10) === index)
      if (row) {
        const pill = row.querySelector("[data-vocabulary-set-generation-target='cardStatus']")
        if (pill) {
          pill.classList.remove("vs-card-row__status--pending")
          pill.classList.add("vs-card-row__status--ready")
          pill.textContent = "Audio ready"
        }
      }
    }
  }

  setProgress(percentage) {
    if (this.hasBarTarget) this.barTarget.style.width = `${percentage}%`
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
